import React, { useCallback, useEffect, useState } from 'react';
import { IconButton, Paper, Typography, useTheme, useMediaQuery, Accordion, AccordionSummary, AccordionDetails, TextField, CircularProgress, Box, Snackbar, Alert, Button, Chip, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { IoCloseOutline } from 'react-icons/io5';
import { IoShareSocialOutline } from 'react-icons/io5';
import requestStore from '../../stores/requestStore';
import objectStore from '../../stores/objectStore';

import ListObjectRequest from '../object/ListObjectRequest';
import { observer } from 'mobx-react-lite';
import ListObjectActiveRequest from '../object/ListObjectActiveRequest';
import logger from '../../utils/logger';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import requestSourceStore from '../../stores/requestSourceStore';
import { canMutateResource } from '../../tenancy/resourcePermissions';
import ClientPicker from '../client/ClientPicker';
import RequestCriteriaEditor from './RequestCriteriaEditor';

function RequestModalCard({
  onClose,
  requestId,
  boardId,
  columnId,
  description,
  title,
  addressShort,
  extraFilter,
  requestFilter,
  jsonFileName,
  countryCodeISO,
  uuid,
  outcome = 'OPEN',
  closedAt = null,
  sourceId = null,
  clientId = null,
  client = null,
  permissions,
}) {
  const mayMutate = canMutateResource({ permissions });
  const [isLoading, setIsLoading] = useState(true); // Флаг загрузки
  const [requestTitle, setRequestTitle] = useState(title); // Локальное состояние для заголовка
  const [requestDescription, setRequestDescription] = useState(description); // Локальное состояние для описания
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Флаг сохранения
  const [saveError, setSaveError] = useState(null); // Ошибка сохранения
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Состояние для уведомления о копировании ссылки
  const [selectedSourceId, setSelectedSourceId] = useState(sourceId || '');
  const [currentOutcome, setCurrentOutcome] = useState(outcome);
  const [currentClosedAt, setCurrentClosedAt] = useState(closedAt);
  const [reopenColumnId, setReopenColumnId] = useState('');
  const [currentClientId, setCurrentClientId] = useState(clientId || '');
  const [linkedClient, setLinkedClient] = useState(client);
  const [editingCriteria, setEditingCriteria] = useState(false);
  const [matchingPending, setMatchingPending] = useState(false);
  const [currentFilter, setCurrentFilter] = useState(requestFilter || {});
  const [currentAddressShort, setCurrentAddressShort] = useState(addressShort || '');
  const normalColumns = requestStore.getBoardsByIdArray(boardId).filter(column => column.systemType === 'NORMAL');

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setIsLoading(true); // Начало загрузки
      await requestStore.fetchRequestObjects(requestId, boardId, columnId);
      if (isMounted) {
        setIsLoading(false); // Завершение загрузки
      }
    };
    fetchData();
    requestSourceStore.fetchSources().catch(() => {});

    return () => {
      isMounted = false;
      logger.log('RequestModalCard размонтируется, очищаем activeRequestObjects');
      requestStore.activeRequestObjects = [];
    };
  }, [requestId, boardId, columnId]);

  useEffect(() => {
    setCurrentClientId(clientId || '');
    setLinkedClient(client || null);
  }, [clientId, client]);

  useEffect(() => { setCurrentFilter(requestFilter || {}); }, [requestFilter]);
  useEffect(() => { setCurrentAddressShort(addressShort || ''); }, [addressShort]);

  const criteriaSaved = async updated => {
    const savedFilter = updated.request?.filter || currentFilter;
    setCurrentFilter(savedFilter);
    setCurrentAddressShort(savedFilter.addressShort || '');
    setEditingCriteria(false);
    setMatchingPending(Boolean(updated.matchingPending));
    await requestStore.fetchRequestObjects(requestId, boardId, columnId);
  };

  const changeClient = async nextClientId => {
    if (!mayMutate || String(nextClientId || '') === String(currentClientId || '')) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await requestStore.updateRequestClient(requestId, nextClientId || null);
      setCurrentClientId(updated.clientId || '');
      setLinkedClient(updated.client || null);
    } catch (error) {
      setSaveError(error.response?.data?.error?.code || 'Не удалось изменить клиента заявки');
    } finally { setIsSaving(false); }
  };

  // Функция для сохранения изменений
  const handleSave = async (field, value) => {
    if (!mayMutate) return;
    if ((field === 'title' && value === title) || (field === 'description' && value === description)) {
      // Не сохраняем, если значение не изменилось
      if (field === 'title') setIsEditingTitle(false);
      if (field === 'description') setIsEditingDescription(false);
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    logger.log(`Saving ${field}: ${value}`);

    try {
      const updates = {};
      updates[field] = value;

      // Вызываем метод стора для обновления
      await requestStore.updateRequestDetails(requestId, boardId, columnId, updates, countryCodeISO);

      // Обновляем локальное состояние после успешного сохранения (на случай если пропсы не сразу обновятся)
      if (field === 'title') {
        setRequestTitle(value); // Обновляем локальное состояние
        setIsEditingTitle(false);
      }
      if (field === 'description') {
        setRequestDescription(value); // Обновляем локальное состояние
        setIsEditingDescription(false);
      }
      if (field === 'sourceId') setSelectedSourceId(value || '');
      logger.log(`${field} updated successfully`);
    } catch (error) {
      logger.error(`Error saving ${field}:`, error);
      setSaveError(`Ошибка сохранения ${field === 'title' ? 'заголовка' : 'описания'}`);
      // Не выходим из режима редактирования при ошибке, чтобы пользователь мог попробовать снова
      // Можно вернуть старое значение, если нужно:
      // if (field === 'title') setRequestTitle(title);
      // if (field === 'description') setRequestDescription(description);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseOutcome = async nextOutcome => {
    if (!mayMutate) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const request = await requestStore.closeRequest(requestId, nextOutcome);
      setCurrentOutcome(request.outcome);
      setCurrentClosedAt(request.closedAt);
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Не удалось закрыть заявку');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReopen = async () => {
    if (!mayMutate || !reopenColumnId) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await requestStore.reopenRequest(requestId, reopenColumnId);
      setCurrentOutcome('OPEN');
      setCurrentClosedAt(null);
      setReopenColumnId('');
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Не удалось открыть заявку заново');
    } finally {
      setIsSaving(false);
    }
  };

  // Функция для форматирования и вывода фильтров
  const renderFilters = () => {
    if (!extraFilter || !jsonFileName || !objectStore.jsonfiles.length) {
      return null;
    }
    logger.log('🏀🏀🏀🏀🏀🏀');
    // Находим нужный JSON файл с определениями
    const jsonDefinition = objectStore.jsonfiles.find(
      (file) => file.type.fileName === jsonFileName,
    );
   
   
    const filterEntries = Object.entries(extraFilter);
    const formattedFilters = [];

    filterEntries.forEach(([key, value]) => {
      // Ищем определение фильтра (ключ вида filter_...)
      const filterDefinitionKey = Object.keys(jsonDefinition).find(
        (defKey) => defKey.startsWith('filter_') && jsonDefinition[defKey].fieldJson === key,
      );

      if (!filterDefinitionKey) {
        //logger.warn(`Не найдено определение фильтра для ключа ${key} в ${jsonFileName}`);
        return; // Пропускаем, если определение не найдено
      }

      const filterDefinition = jsonDefinition[filterDefinitionKey];
      const label = filterDefinition.label || key; // Используем label или ключ как запасной вариант

      // Обработка типа interval
      if (filterDefinition.filterType === 'interval' && value && typeof value === 'object') {
        const from = value.from !== undefined ? `от ${value.from}` : '';
        const to = value.to !== undefined ? `до ${value.to}` : '';
        if (from || to) {
          formattedFilters.push(`${label}: ${from} ${to}`.trim());
        }
      }
      // Обработка типа enum_in
      else if (
        filterDefinition.filterType === 'enum_in' &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        // Находим определение поля (ключ совпадает с key)
        const fieldDefinition = jsonDefinition[key];
        if (fieldDefinition && fieldDefinition.options && Array.isArray(fieldDefinition.options)) {
          const valueLabels = value
            .map((id) => {
              const option = fieldDefinition.options.find((opt) => opt.id === id);
              return option ? option.value : null; // Возвращаем value или null, если id не найден
            })
            .filter((label) => label !== null); // Убираем ненайденные значения

          if (valueLabels.length > 0) {
            formattedFilters.push(`${label}: ${valueLabels.join(', ')}`);
          }
        } else {
          logger.warn(`Не найдены options для enum_in ключа ${key} в ${jsonFileName}`);
        }
      }
      // Обработка типа bool_ge
      else if (filterDefinition.filterType === 'bool_ge' && value === true) {
        formattedFilters.push(label);
      }
      // Обработка типа array_includes_any (логика как у enum_in)
      else if (
        filterDefinition.filterType === 'array_includes_any' &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        // Находим определение поля (ключ совпадает с key)
        const fieldDefinition = jsonDefinition[key];
        if (fieldDefinition && fieldDefinition.options && Array.isArray(fieldDefinition.options)) {
          const valueLabels = value
            .map((id) => {
              const option = fieldDefinition.options.find((opt) => opt.id === id);
              return option ? option.value : null;
            })
            .filter((label) => label !== null);

          if (valueLabels.length > 0) {
            formattedFilters.push(`${label}: ${valueLabels.join(', ')}`);
          }
        } else {
          logger.warn(`Не найдены options для array_includes_any ключа ${key} в ${jsonFileName}`);
        }
      }
      // Обработка типа text_includes
      else if (
        filterDefinition.filterType === 'text_includes' &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        const keywords = value.map(word => `<b>${word}</b>`).join(', ');
        formattedFilters.push(`${label}: ${keywords}`);
      }
      // Обработка типа text_excludes
      else if (
        filterDefinition.filterType === 'text_excludes' &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        const keywords = value.map(word => `<b>${word}</b>`).join(', ');
        formattedFilters.push(`${label}: ${keywords}`);
      }
      // Добавить сюда другие типы фильтров по мере необходимости
    });

    return formattedFilters.map((filterString, index) => (
      // Используем dangerouslySetInnerHTML для рендера HTML (жирного шрифта)
      <div 
        key={index} 
        className={`mb-1 ${isMobile ? 'text-sm' : 'text-sm'} font-thin`} 
        dangerouslySetInnerHTML={{ __html: filterString }} 
      />
    ));
  };

  // Функция для генерации и копирования ссылки на заявку
  const handleShareUrlGenerate = () => {
    logger.log('handleShareUrlGenerate');

    // Формируем ссылку для шаринга
    const url = `${import.meta.env.VITE_OPEN_APP_URL}/request/${uuid}`;

    // Копируем ссылку в буфер обмена
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setSnackbarOpen(true);
        })
        .catch((error) => {
          logger.log(`Ошибка при копировании ссылки: ${error}`);
        });
    }
  };

  // Обработчик закрытия снэкбара
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <div
      style={{
        position: 'relative',
        padding: 0,
        width: '100%',
        maxHeight: isMobile ? '100vh' : 'auto',
        overflowY: 'auto',
      }}>
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && (
        <div onClick={() => logger.log(countryCodeISO)}>% RequestModalCard %</div>
      )}
      <div>Заявка</div>
      <div className="flex flex-row justify-end space-x-2">
        {mayMutate && <Button onClick={handleShareUrlGenerate}>
          <IoShareSocialOutline className="text-xl" />
        </Button>}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            logger.log('Клик по кнопке закрытия');
            onClose();
          }}
          style={{
            zIndex: 10,
          }}>
          <IoCloseOutline size={24} />
        </IconButton>
      </div>
      <div style={{ marginBottom: isMobile ? '60px' : '20px' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Chip
            color={currentOutcome === 'OPEN' ? 'default' : (currentOutcome === 'WON' ? 'success' : 'error')}
            label={currentOutcome === 'OPEN' ? 'В работе' : (currentOutcome === 'WON' ? 'Успешно' : 'Неуспешно')}
          />
          {currentClosedAt && <Typography variant="caption">Закрыта {new Date(currentClosedAt).toLocaleString()}</Typography>}
          {currentOutcome === 'OPEN' ? (
            <>
              {mayMutate && <Button size="small" color="success" disabled={isSaving} onClick={() => handleCloseOutcome('WON')}>Закрыть успешно</Button>}
              {mayMutate && <Button size="small" color="error" disabled={isSaving} onClick={() => handleCloseOutcome('LOST')}>Закрыть неуспешно</Button>}
            </>
          ) : (
            <>
              {mayMutate && <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="reopen-column-label">Колонка для возврата</InputLabel>
                <Select labelId="reopen-column-label" label="Колонка для возврата" value={reopenColumnId} onChange={event => setReopenColumnId(event.target.value)}>
                  {normalColumns.map(column => <MenuItem key={column.columnId} value={column.columnId}>{column.name}</MenuItem>)}
                </Select>
              </FormControl>}
              {mayMutate && <Button size="small" disabled={!reopenColumnId || isSaving} onClick={handleReopen}>Открыть заново</Button>}
            </>
          )}
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Клиент</Typography>
          {currentClientId && linkedClient && <Box sx={{ mt: 0.5 }}>
            <Typography>{linkedClient.name}</Typography>
            {linkedClient.phone && <Typography variant="body2" color="text.secondary">{linkedClient.phone}</Typography>}
            {linkedClient.email && <Typography variant="body2" color="text.secondary">{linkedClient.email}</Typography>}
            {linkedClient.archivedAt && <Chip size="small" color="warning" label="Клиент в архиве" sx={{ mt: 0.5 }} />}
          </Box>}
          {currentClientId && !linkedClient && <Typography variant="body2" color="text.secondary">Клиент недоступен.</Typography>}
          {!currentClientId && <Typography variant="body2" color="text.secondary">Клиент не указан.</Typography>}
          {mayMutate && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 260, flexGrow: 1 }}><ClientPicker value={currentClientId} selectedClient={linkedClient} onChange={changeClient} disabled={isSaving} label={currentClientId ? 'Изменить клиента' : 'Выбрать клиента'} /></Box>
            {currentClientId && <Button size="small" disabled={isSaving} onClick={() => changeClient(null)}>Отвязать</Button>}
          </Box>}
        </Box>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="request-source-edit-label">Источник</InputLabel>
          <Select
            labelId="request-source-edit-label"
            label="Источник"
            value={selectedSourceId}
            disabled={!mayMutate}
            onChange={event => { const value = event.target.value; setSelectedSourceId(value); handleSave('sourceId', value || null); }}>
            <MenuItem value=""><em>Не указан</em></MenuItem>
            {requestSourceStore.sources.map(source => <MenuItem key={source.id} value={source.id}>{source.name}</MenuItem>)}
          </Select>
        </FormControl>
        {/* Редактируемый заголовок */}
        <Box sx={{ position: 'relative', mb: 1 }} className={`mt-2 ${isMobile ? 'text-xl' : 'text-lg'}`}>
          {isEditingTitle ? (
            <TextField
              fullWidth
              variant="standard"
              value={requestTitle}
              onChange={(e) => setRequestTitle(e.target.value)}
              onBlur={() => handleSave('title', requestTitle)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); // Предотвращаем стандартное поведение Enter
                  handleSave('title', requestTitle);
                } else if (e.key === 'Escape') {
                   setRequestTitle(title); // Возвращаем исходное значение
                   setIsEditingTitle(false);
                }
              }}
              autoFocus
              disabled={isSaving || !mayMutate}
              size="small" // Меньший размер
              sx={{ '& .MuiInput-input': { fontSize: isMobile ? '1.25rem' : '1.125rem', lineHeight: '1.6' } }} // Стили для инпута
            />
          ) : (
            <Typography variant="inherit" onClick={mayMutate ? () => {setIsEditingTitle(true); setRequestTitle(title);} : undefined} sx={{ cursor: mayMutate ? 'pointer' : 'default', display: 'inline-block', width: '100%' }}>
              {requestTitle || title} {/* Показываем локальное или из пропсов */}
            </Typography>
          )}
          {isSaving && isEditingTitle && <CircularProgress size={20} sx={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)' }} />}
        </Box>

        {/* Редактируемое описание */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          {isEditingDescription ? (
            <TextField
              fullWidth
              multiline
              variant="standard"
              value={requestDescription}
              onChange={(e) => setRequestDescription(e.target.value)}
              onBlur={() => handleSave('description', requestDescription)}
               onKeyDown={(e) => {
                 if (e.key === 'Escape') {
                   setRequestDescription(description); // Возвращаем исходное значение
                   setIsEditingDescription(false);
                 }
                 // Оставляем стандартное поведение Enter для многострочного поля
               }}
              autoFocus
              disabled={isSaving || !mayMutate}
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', lineHeight: '1.43', fontFamily: theme.typography.fontFamily, fontWeight: 300 } }} // Стили для инпута
            />
          ) : (
            <Typography
              variant="body2" // Используем body2 для совпадения со стилями ниже
              onClick={mayMutate ? () => {setIsEditingDescription(true); setRequestDescription(description);} : undefined}
              sx={{ cursor: 'pointer', whiteSpace: 'pre-line', fontWeight: 300, display: 'inline-block', width: '100%', minHeight: '1.43em' /* Минимальная высота */ }}
              className={`${isMobile ? 'text-sm' : 'text-sm'}`}
            >
              {requestDescription || description || 'Добавить описание...'} {/* Показываем локальное, из пропсов или плейсхолдер */}
            </Typography>
          )}
           {isSaving && isEditingDescription && <CircularProgress size={20} sx={{ position: 'absolute', right: 5, top: 10 }} />}
        </Box>

        {/* Сообщение об ошибке сохранения */}
         {saveError && <Typography color="error" variant="caption">{saveError}</Typography>}

        <div className={`mb-4 ${isMobile ? 'text-sm' : 'text-sm'} font-thin whitespace-pre-line`}>
          {currentAddressShort}
        </div>
		{mayMutate && !editingCriteria && <Button size="small" variant="outlined" onClick={() => setEditingCriteria(true)} sx={{ mb: 2 }}>Изменить критерии подбора</Button>}
		{matchingPending && <Alert severity="info" sx={{ mb: 2 }}>Критерии сохранены. Автоматические совпадения пересчитываются в фоне; обновите карточку позже.</Alert>}
		{editingCriteria && <RequestCriteriaEditor
			request={{ id: requestId, filter: currentFilter, countryCodeISO }}
			onCancel={() => setEditingCriteria(false)}
			onSaved={criteriaSaved}
		/>}
        {/* Вывод отформатированных фильтров в аккордеоне */}
        {(extraFilter && Object.keys(extraFilter).length > 0) && ( // Показываем аккордеон только если есть фильтры
             <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' }, mb: 0 }}>
                 <AccordionSummary
                     expandIcon={<ExpandMoreIcon />}
                     aria-controls="filters-content"
                     id="filters-header"
                     sx={{
                         flexDirection: 'row-reverse',
                         minHeight: 'auto', 
                         '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                             transform: 'rotate(90deg)',
                         },
                         '& .MuiAccordionSummary-content': {
                             marginLeft: theme.spacing(1),
                             margin: '8px 0',
                             paddingLeft: 0,
                         },
                         paddingLeft: 0,
                     }}
                 >
                     <Typography variant="subtitle2">Фильтры</Typography>
                 </AccordionSummary>
                 <AccordionDetails sx={{ p: 0 }}> {/* Убираем внутренние отступы */}
                     <div className="mb-4 pl-4">{renderFilters()}</div>
                 </AccordionDetails>
             </Accordion>
        )}

        <p></p>
        {isLoading ? (
          <p>Загрузка объектов...</p> // Индикатор загрузки
        ) : (
          <ListObjectActiveRequest
            objects={requestStore.activeRequestObjects}
            requestId={requestId}
          />
        )}
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Ссылка скопирована в буфер обмена
        </Alert>
      </Snackbar>
    </div>
  );
}

export default observer(RequestModalCard);
